import { useState } from 'react';
import { Modal } from './Modal';

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  canvasRef: any;
  songTitle: string;
}

export function ExportModal({ open, onClose, songTitle }: ExportModalProps) {
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const canvas = document.querySelector('.relative.bg-white') as HTMLElement;
      if (!canvas) return;
      
      const { default: html2canvas } = await import('html2canvas');
      const { default: jsPDF } = await import('jspdf');
      
      const c = await html2canvas(canvas, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = c.toDataURL('image/png');
      
      const pdf = new jsPDF({
        orientation,
        unit: 'pt',
        format: 'letter',
      });
      
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pdfW / c.width, pdfH / c.height);
      const imgW = c.width * ratio;
      const imgH = c.height * ratio;
      const x = (pdfW - imgW) / 2;
      
      if (imgH <= pdfH) {
        pdf.addImage(imgData, 'PNG', x, 0, imgW, imgH);
      } else {
        let yOffset = 0;
        let page = 0;
        while (yOffset < c.height) {
          if (page > 0) pdf.addPage();
          const srcY = yOffset;
          const srcH = Math.min(c.height - yOffset, pdfH / ratio);
          
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = c.width;
          pageCanvas.height = srcH;
          const ctx = pageCanvas.getContext('2d')!;
          ctx.drawImage(c, 0, srcY, c.width, srcH, 0, 0, c.width, srcH);
          
          const pageImg = pageCanvas.toDataURL('image/png');
          pdf.addImage(pageImg, 'PNG', x, 0, imgW, srcH * ratio);
          
          yOffset += srcH;
          page++;
        }
      }
      
      pdf.save(`${songTitle.replace(/\s+/g, '_')}_chart.pdf`);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Export PDF">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Orientation</label>
          <div className="flex gap-2">
            <button
              onClick={() => setOrientation('portrait')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${orientation === 'portrait' ? 'bg-primary-600 text-white' : 'bg-surface-800 text-gray-400 hover:bg-surface-700'}`}
            >
              Portrait
            </button>
            <button
              onClick={() => setOrientation('landscape')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${orientation === 'landscape' ? 'bg-primary-600 text-white' : 'bg-surface-800 text-gray-400 hover:bg-surface-700'}`}
            >
              Landscape
            </button>
          </div>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
        >
          {exporting ? 'Exporting...' : 'Download PDF'}
        </button>
      </div>
    </Modal>
  );
}
